import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import AttendanceCalendar from "../../components/attendance/AttendanceCalendar";
import { exportAttendancePDF } from "../../components/attendance/exportAttendancePDF";

const STATUS = {
  full:     { label:"Full Day",  icon:"✓", color:"#059669", bg:"#D1FAE5", border:"#A7F3D0" },
  half:     { label:"Half Day",  icon:"½", color:"#D97706", bg:"#FEF3C7", border:"#FDE68A" },
  overtime: { label:"Overtime",  icon:"⏱", color:"#0369A1", bg:"#DBEAFE", border:"#93C5FD" },
  absent:   { label:"Absent",    icon:"✗", color:"#DC2626", bg:"#FEF2F2", border:"#FECACA" },
  holiday:  { label:"Holiday",   icon:"★", color:"#B45309", bg:"#FEF3C7", border:"#FDE68A" },
  leave:    { label:"Leave",     icon:"✈", color:"#7C3AED", bg:"#EDE9FE", border:"#DDD6FE" },
};
const WORKED = ["full","half","overtime"];

const navItems = [
  { label:"Dashboard",  emoji:"🏠", path:"/carpenter/dashboard" },
  { label:"My Labours", emoji:"👷", path:"/carpenter/labours" },
  { label:"Add Labour", emoji:"➕", path:"/carpenter/add-labour" },
  { label:"Attendance", emoji:"📋", path:"/carpenter/attendance", active:true },
  { label:"Payments",   emoji:"💰", path:"/carpenter/payments" },
  { label:"Salary",     emoji:"💵", path:"/carpenter/salary" },
];

export default function LabourAttendance() {
  const { labourId } = useParams();
  const navigate     = useNavigate();

  const [records,     setRecords]     = useState([]);
  const [payments,    setPayments]    = useState([]);
  const [labourName,  setLabourName]  = useState("");
  const [labourEmail, setLabourEmail] = useState("");
  const [dailyRate,   setDailyRate]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState("all");
  const [month,       setMonth]       = useState("");
  const [view,        setView]        = useState("list");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [attRes, payRes] = await Promise.all([
        api.get(`/attendance/labour/${labourId}`),
        api.get(`/payment/${labourId}`),
      ]);
      setRecords(attRes.data.data        || []);
      setLabourName(attRes.data.labourName   || "Labour");
      setLabourEmail(attRes.data.labourEmail || "");
      setDailyRate(attRes.data.dailyRate     ?? null);
      setPayments(payRes.data.data           || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ── Stats ──────────────────────────────────────────────────────
  const totalDays    = records.length;
  const workedDays   = records.filter(r => WORKED.includes(r.status)).length;
  const fullDays     = records.filter(r => r.status==="full").length;
  const halfDays     = records.filter(r => r.status==="half").length;
  const overtimeDays = records.filter(r => r.status==="overtime").length;
  const absentDays   = records.filter(r => r.status==="absent").length;
  const holidayDays  = records.filter(r => r.status==="holiday").length;
  const leaveDays    = records.filter(r => r.status==="leave").length;
  const pct          = totalDays ? Math.round((workedDays/totalDays)*100) : 0;
  const pctColor     = pct>=75?"#059669":pct>=50?"#D97706":"#DC2626";

  const streak = (() => {
    if(!records.length) return 0;
    const s = [...records].sort((a,b)=>new Date(b.date)-new Date(a.date));
    let c=0; for(const r of s){ if(WORKED.includes(r.status)) c++; else break; } return c;
  })();

  const months   = [...new Set(records.map(r=>r.date?.slice(0,7)))].sort().reverse();
  const filtered = records.filter(r=>{
    const sOk = filter==="all" || r.status===filter;
    const mOk = !month || r.date?.startsWith(month);
    return sOk && mOk;
  });

  // Payment map for list view rows
  const payMap = {};
  payments.forEach(p=>{
    const d   = new Date(p.date||p.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    if(!payMap[key]) payMap[key]={otAmt:0,advAmt:0};
    if(p.type==="overtime") payMap[key].otAmt  += p.amount;
    if(p.type==="advance")  payMap[key].advAmt += p.amount;
  });

  const ini     = n => n?.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()||"LA";
  const aColors = ["#D97706","#0369A1","#059669","#7C3AED","#DC2626","#0891B2"];
  const aColor  = id => aColors[id?.charCodeAt(0)%aColors.length]||"#D97706";

  const Badge = ({status, overtimeHours})=>{
    const cfg=STATUS[status]||STATUS.absent;
    const ex=status==="overtime"&&overtimeHours?` (${overtimeHours}h)`:"";
    return <span style={{display:"inline-flex",alignItems:"center",gap:"4px",padding:"3px 10px",borderRadius:"20px",fontSize:"11px",fontWeight:"700",background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`}}>{cfg.icon} {cfg.label}{ex}</span>;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Plus Jakarta Sans',sans-serif;background:#FAF7F4}
        .sidebar{width:220px;flex-shrink:0;background:#fff;border-right:1.5px solid #E7E5E4;padding:24px 14px;display:flex;flex-direction:column;gap:6px;position:sticky;top:0;height:100vh;overflow-y:auto}
        .topbar{display:none}
        .hamburger{display:none;align-items:center;justify-content:center;width:40px;height:40px;border-radius:10px;border:1.5px solid #E7E5E4;background:#fff;cursor:pointer;flex-shrink:0}
        .sidebar-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:40}
        .nav-btn{display:flex;align-items:center;gap:9px;width:100%;padding:10px 12px;border-radius:10px;border:none;background:none;font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;font-weight:600;color:#78716C;cursor:pointer;transition:background .15s,color .15s;text-align:left}
        .nav-btn:hover,.nav-btn.active{background:#FEF3C7;color:#D97706}
        .nav-btn.danger:hover{background:#FEF2F2;color:#DC2626}
        .vtab{padding:7px 16px;border-radius:8px;border:none;font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:all .15s;color:#78716C;background:transparent}
        .vtab.active{background:#fff;color:#1C1917;box-shadow:0 1px 4px rgba(0,0,0,.08)}
        .ftab{padding:6px 14px;border-radius:20px;border:1.5px solid #E7E5E4;font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:all .15s;background:#fff;color:#78716C}
        .ftab.f-all{background:#FEF3C7;border-color:#FDE68A;color:#D97706}
        .ftab.f-full{background:#D1FAE5;border-color:#A7F3D0;color:#059669}
        .ftab.f-half{background:#FEF3C7;border-color:#FDE68A;color:#D97706}
        .ftab.f-overtime{background:#DBEAFE;border-color:#93C5FD;color:#0369A1}
        .ftab.f-absent{background:#FEF2F2;border-color:#FECACA;color:#DC2626}
        .ftab.f-holiday{background:#FEF3C7;border-color:#FDE68A;color:#B45309}
        .ftab.f-leave{background:#EDE9FE;border-color:#DDD6FE;color:#7C3AED}
        .row{display:grid;grid-template-columns:1.8fr 1.4fr 1.2fr 1.1fr;align-items:center;padding:11px 20px;border-bottom:1px solid #F5F5F4;transition:background .12s;animation:fadeUp .25s ease both}
        .row:hover{background:#FFFBF5}
        .row:last-child{border-bottom:none}
        .chip{display:inline-flex;align-items:center;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700}
        @media(max-width:768px){
          .sidebar{position:fixed;left:0;top:0;z-index:50;transform:translateX(-100%);transition:transform .28s ease;height:100%}
          .sidebar.open{transform:translateX(0)}
          .sidebar-overlay.open{display:block}
          .topbar{display:flex;align-items:center;gap:12px;padding:14px 16px;background:#fff;border-bottom:1.5px solid #E7E5E4;position:sticky;top:0;z-index:30}
          .hamburger{display:flex}
          .main-wrap{flex-direction:column!important}
          .main-content{padding:16px!important}
          .stats-grid{grid-template-columns:repeat(3,1fr)!important}
          .row{grid-template-columns:1.5fr 1fr 1fr!important}
          .row .day-col{display:none}
          .action-bar,.filter-bar{flex-direction:column!important}
        }
        @media(max-width:420px){
          .main-content{padding:12px!important}
          .hero-inner{flex-direction:column!important}
          .stats-grid{grid-template-columns:1fr 1fr!important}
        }
      `}</style>

      <div className="main-wrap" style={{display:"flex",minHeight:"100vh",fontFamily:"'Plus Jakarta Sans',sans-serif",background:"#FAF7F4"}}>
        <div className={`sidebar-overlay ${sidebarOpen?"open":""}`} onClick={()=>setSidebarOpen(false)}/>

        <aside className={`sidebar ${sidebarOpen?"open":""}`}>
          <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"28px",padding:"0 4px"}}>
            <div style={{width:"36px",height:"36px",background:"linear-gradient(135deg,#92400E,#D97706)",borderRadius:"10px",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{fontSize:"18px"}}>🪚</span>
            </div>
            <div>
              <p style={{fontWeight:"800",fontSize:"15px",color:"#1C1917"}}>Craftigo</p>
              <p style={{fontSize:"11px",color:"#A8A29E",fontWeight:"500"}}>Carpenter Panel</p>
            </div>
          </div>
          <p style={{fontSize:"11px",fontWeight:"700",color:"#C4B5A4",letterSpacing:"0.7px",textTransform:"uppercase",padding:"0 6px",marginBottom:"6px"}}>Menu</p>
          {navItems.map(n=>(
            <button key={n.label} className={`nav-btn ${n.active?"active":""}`} onClick={()=>{navigate(n.path);setSidebarOpen(false);}}>
              {n.emoji} {n.label}
            </button>
          ))}
          <div style={{flex:1}}/>
          <div style={{padding:"12px",background:"linear-gradient(135deg,#FEF3C7,#FFF7ED)",borderRadius:"12px",border:"1px solid #FDE68A",marginBottom:"10px"}}>
            <p style={{fontSize:"12px",fontWeight:"700",color:"#92400E"}}>🪚 Carpenter</p>
            <p style={{fontSize:"11px",color:"#B45309",marginTop:"2px"}}>Manage your team</p>
          </div>
          <button className="nav-btn danger" onClick={()=>{localStorage.clear();navigate("/login");}}>🚪 Logout</button>
        </aside>

        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
          <div className="topbar">
            <button className="hamburger" onClick={()=>setSidebarOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="3" y1="6" x2="21" y2="6" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="12" x2="21" y2="12" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="18" x2="21" y2="18" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <span style={{fontWeight:"800",fontSize:"16px",color:"#1C1917"}}>📋 Attendance History</span>
          </div>

          <main className="main-content" style={{padding:"28px 32px",flex:1,overflowY:"auto"}}>
            <div style={{marginBottom:"20px",animation:"fadeUp .3s ease both"}}>
              <button onClick={()=>navigate("/carpenter/labours")}
                style={{display:"inline-flex",alignItems:"center",gap:"6px",padding:"7px 14px",background:"#fff",border:"1.5px solid #E7E5E4",borderRadius:"10px",fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"13px",fontWeight:"600",color:"#78716C",cursor:"pointer"}}>
                ← Back to Labours
              </button>
            </div>

            {loading?(
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"80px 0"}}>
                <div style={{width:"36px",height:"36px",border:"3px solid #FDE68A",borderTop:"3px solid #D97706",borderRadius:"50%",animation:"spin .7s linear infinite",marginBottom:"14px"}}/>
                <p style={{color:"#A8A29E",fontSize:"14px"}}>Loading records…</p>
              </div>
            ):(
              <>
                {/* HERO */}
                <div style={{background:"linear-gradient(135deg,#92400E,#B45309,#D97706)",borderRadius:"22px",padding:"24px 28px",marginBottom:"20px",animation:"fadeUp .35s .05s ease both"}}>
                  <div className="hero-inner" style={{display:"flex",alignItems:"center",gap:"20px",flexWrap:"wrap"}}>
                    <div style={{width:"64px",height:"64px",borderRadius:"18px",background:`${aColor(labourId)}30`,border:"2.5px solid rgba(255,255,255,.4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"24px",fontWeight:"800",color:"#fff",flexShrink:0}}>
                      {ini(labourName)}
                    </div>
                    <div style={{flex:1,minWidth:"130px"}}>
                      <p style={{fontSize:"20px",fontWeight:"800",color:"#fff"}}>{labourName}</p>
                      {labourEmail&&<p style={{fontSize:"12px",color:"rgba(255,255,255,.65)",marginTop:"2px"}}>{labourEmail}</p>}
                      <div style={{display:"flex",gap:"10px",marginTop:"8px",flexWrap:"wrap"}}>
                        {dailyRate!=null&&<span style={{fontSize:"12px",fontWeight:"700",background:"rgba(255,255,255,.2)",color:"#fff",padding:"3px 10px",borderRadius:"20px"}}>₹{dailyRate}/day</span>}
                        {streak>0&&<span style={{fontSize:"12px",fontWeight:"700",background:"rgba(255,255,255,.2)",color:"#fff",padding:"3px 10px",borderRadius:"20px"}}>🔥 {streak}-day streak</span>}
                        <span style={{fontSize:"12px",fontWeight:"700",background:"rgba(255,255,255,.2)",color:"#fff",padding:"3px 10px",borderRadius:"20px"}}>📅 {totalDays} records</span>
                      </div>
                    </div>
                    <div style={{background:"rgba(255,255,255,.18)",borderRadius:"16px",padding:"14px 22px",textAlign:"center",flexShrink:0}}>
                      <p style={{fontSize:"32px",fontWeight:"800",color:"#fff",lineHeight:"1"}}>{pct}%</p>
                      <p style={{fontSize:"11px",color:"rgba(255,255,255,.75)",marginTop:"4px",fontWeight:"600"}}>Attendance Rate</p>
                    </div>
                  </div>
                  {totalDays>0&&(
                    <div style={{marginTop:"16px"}}>
                      <div style={{height:"6px",background:"rgba(255,255,255,.25)",borderRadius:"99px",overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${pct}%`,background:"rgba(255,255,255,.9)",borderRadius:"99px",transition:"width .6s ease"}}/>
                      </div>
                    </div>
                  )}
                </div>

                {/* 6 STAT CARDS */}
                <div className="stats-grid" style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:"8px",marginBottom:"20px"}}>
                  {[
                    {label:"Full Day", value:fullDays,     ...STATUS.full    },
                    {label:"Half Day", value:halfDays,     ...STATUS.half    },
                    {label:"Overtime", value:overtimeDays, ...STATUS.overtime},
                    {label:"Absent",   value:absentDays,   ...STATUS.absent  },
                    {label:"Holiday",  value:holidayDays,  ...STATUS.holiday },
                    {label:"Leave",    value:leaveDays,    ...STATUS.leave   },
                  ].map((s,i)=>(
                    <div key={s.label} style={{background:s.bg,border:`1.5px solid ${s.border}`,borderRadius:"12px",padding:"11px 12px",animation:`fadeUp .4s ${.1+i*.04}s ease both`}}>
                      <p style={{fontSize:"20px",fontWeight:"800",color:s.color,lineHeight:"1"}}>{s.value}</p>
                      <p style={{fontSize:"10px",color:"#78716C",fontWeight:"700",marginTop:"3px"}}>{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* PROGRESS */}
                {totalDays>0&&(
                  <div style={{background:"#fff",border:"1.5px solid #E7E5E4",borderRadius:"14px",padding:"13px 18px",marginBottom:"16px",animation:"fadeUp .4s .28s ease both"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:"7px"}}>
                      <span style={{fontSize:"12px",fontWeight:"700",color:"#44403C"}}>Attendance Rate</span>
                      <span style={{fontSize:"12px",fontWeight:"800",padding:"2px 10px",borderRadius:"20px",background:pct>=75?"#D1FAE5":pct>=50?"#FEF3C7":"#FEF2F2",color:pctColor}}>{pct}%</span>
                    </div>
                    <div style={{height:"8px",background:"#F3F4F6",borderRadius:"99px",overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:"linear-gradient(90deg,#B45309,#D97706)",borderRadius:"99px",transition:"width .6s ease"}}/>
                    </div>
                    <p style={{fontSize:"11px",color:"#A8A29E",marginTop:"5px"}}>{workedDays} worked days (Full + Half + Overtime) out of {totalDays} total</p>
                  </div>
                )}

                {/* ACTION BAR */}
                <div className="action-bar" style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"16px",animation:"fadeUp .4s .3s ease both"}}>
                  <div style={{display:"flex",background:"#F5F5F4",borderRadius:"10px",padding:"3px",gap:"2px"}}>
                    <button className={`vtab ${view==="list"?"active":""}`}     onClick={()=>setView("list")}>📋 List</button>
                    <button className={`vtab ${view==="calendar"?"active":""}`} onClick={()=>setView("calendar")}>📅 Calendar</button>
                  </div>
                  <div style={{flex:1}}/>
                  <button
                    onClick={()=>exportAttendancePDF(labourName,labourEmail,dailyRate,records,month,payments)}
                    style={{display:"flex",alignItems:"center",gap:"7px",padding:"9px 16px",background:"#fff",border:"1.5px solid #E7E5E4",borderRadius:"10px",fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"13px",fontWeight:"700",color:"#44403C",cursor:"pointer",whiteSpace:"nowrap"}}
                    onMouseEnter={e=>{e.currentTarget.style.background="#FEF3C7";e.currentTarget.style.borderColor="#FDE68A";e.currentTarget.style.color="#D97706";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.borderColor="#E7E5E4";e.currentTarget.style.color="#44403C";}}>
                    ⬇️ Download PDF
                  </button>
                </div>

                {/* FILTER BAR */}
                {view==="list"&&(
                  <div className="filter-bar" style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"14px",flexWrap:"wrap",animation:"fadeUp .4s .33s ease both"}}>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                      {[
                        {key:"all",      label:`All (${totalDays})`      },
                        {key:"full",     label:`Full (${fullDays})`      },
                        {key:"half",     label:`Half (${halfDays})`      },
                        {key:"overtime", label:`OT (${overtimeDays})`    },
                        {key:"absent",   label:`Absent (${absentDays})`  },
                        {key:"holiday",  label:`Holiday (${holidayDays})`},
                        {key:"leave",    label:`Leave (${leaveDays})`    },
                      ].map(tab=>(
                        <button key={tab.key} className={`ftab ${filter===tab.key?`f-${tab.key}`:""}`} onClick={()=>setFilter(tab.key)}>
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    {months.length>0&&(
                      <select value={month} onChange={e=>setMonth(e.target.value)}
                        style={{marginLeft:"auto",padding:"8px 12px",border:"1.5px solid #E7E5E4",borderRadius:"10px",fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"13px",fontWeight:"600",color:"#44403C",background:"#fff",cursor:"pointer",outline:"none"}}>
                        <option value="">All Months</option>
                        {months.map(m=><option key={m} value={m}>{new Date(m+"-01").toLocaleDateString("en-IN",{month:"long",year:"numeric"})}</option>)}
                      </select>
                    )}
                  </div>
                )}

                {/* CALENDAR VIEW */}
                {view==="calendar"&&(
                  <div style={{animation:"fadeUp .35s ease both"}}>
                    <AttendanceCalendar records={records}/>
                  </div>
                )}

                {/* LIST VIEW */}
                {view==="list"&&(
                  <div style={{background:"#fff",border:"1.5px solid #E7E5E4",borderRadius:"18px",overflow:"hidden",animation:"fadeUp .4s .36s ease both"}}>
                    {/* Header */}
                    <div className="row" style={{background:"#FAF7F4",borderBottom:"1.5px solid #E7E5E4",animation:"none"}}>
                      <span style={{fontSize:"11px",fontWeight:"700",color:"#A8A29E",letterSpacing:"0.6px",textTransform:"uppercase"}}>Date</span>
                      <span className="day-col" style={{fontSize:"11px",fontWeight:"700",color:"#A8A29E",letterSpacing:"0.6px",textTransform:"uppercase"}}>Day</span>
                      <span style={{fontSize:"11px",fontWeight:"700",color:"#A8A29E",letterSpacing:"0.6px",textTransform:"uppercase"}}>Status</span>
                      <span style={{fontSize:"11px",fontWeight:"700",color:"#A8A29E",letterSpacing:"0.6px",textTransform:"uppercase"}}>Payment</span>
                    </div>

                    {filtered.length===0?(
                      <div style={{padding:"52px",textAlign:"center"}}>
                        <p style={{fontSize:"32px",marginBottom:"10px"}}>📭</p>
                        <p style={{color:"#78716C",fontWeight:"700",fontSize:"15px"}}>No records found</p>
                        <p style={{color:"#A8A29E",fontSize:"13px",marginTop:"4px"}}>Try changing filters</p>
                      </div>
                    ):(
                      filtered.map((r,i)=>{
                        const d   = new Date(r.date);
                        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                        const pay = payMap[key]||{otAmt:0,advAmt:0};
                        return (
                          <div key={r._id||i} className="row" style={{animationDelay:`${i*.025}s`}}>
                            <span style={{fontSize:"14px",fontWeight:"600",color:"#1C1917"}}>
                              {d.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                            </span>
                            <span className="day-col" style={{fontSize:"13px",color:"#78716C",fontWeight:"500"}}>
                              {d.toLocaleDateString("en-IN",{weekday:"long"})}
                            </span>
                            <Badge status={r.status} overtimeHours={r.overtimeHours}/>
                            {/* Payment chips */}
                            <div style={{display:"flex",gap:"4px",flexWrap:"wrap",alignItems:"center"}}>
                              {pay.otAmt>0&&(
                                <span className="chip" style={{background:"#DBEAFE",color:"#0369A1",border:"1px solid #93C5FD"}}>
                                  +₹{pay.otAmt} OT
                                </span>
                              )}
                              {pay.advAmt>0&&(
                                <span className="chip" style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA"}}>
                                  -₹{pay.advAmt} Adv
                                </span>
                              )}
                              {!pay.otAmt&&!pay.advAmt&&(
                                <span style={{fontSize:"11px",color:"#D1D5DB"}}>—</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}

                    {filtered.length>0&&(
                      <div style={{padding:"12px 20px",borderTop:"1px solid #F5F5F4",textAlign:"center"}}>
                        <p style={{fontSize:"12px",color:"#A8A29E",fontWeight:"500"}}>
                          Showing {filtered.length} of {totalDays} records
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </>
  );
}